/* eslint-disable max-lines */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import cn from 'classnames';
import { Folder } from '../../../utils/types/folders/folder';
import {
  DocumentPayload,
  useMergeDocuments
} from '../../../utils/api/documents/use-merge-documents';
import { AxiosError } from 'axios';
import { BaseError } from '../../../utils/types/shared/base';
import { AlertTriangle, ExternalLink, Square, Trash2, X } from 'lucide-react';
import { useThemeContext } from '../../../utils/providers/theme-context';
import Modal from '../modals/modal';
import Button from '../ui-kit/button';
import Input from '../ui-kit/input';
import { MoveFolderPicker } from './move-folder-picker';

// Mock GridApi interface to replace ag-grid dependency
interface MockGridApi {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getSelectedNodes: () => Array<{ data: any }>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getRenderedNodes: () => Array<{ data: any }>;
  deselectAll: () => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  setServerSideSelectionState: (state: any) => void;
}

interface Props<T> {
  selectedState: T[] | null;
  deleteRows?: (rows: T[], deleteAllRows?: boolean) => Promise<void>;
  isDeleting?: boolean;
  isMoving?: boolean;
  moveRows?: (rows: T[], moveAllRows: boolean) => Promise<void>;
  isMerging?: boolean;
  gridApi: MockGridApi;
  folders?: Folder[];
  selectedToMove?: Folder | null;
  setSelectedToMove?: React.Dispatch<React.SetStateAction<Folder | undefined>>;
  currentFolderId?: number;
  canMoveToFolder?: (rows: T[], targetId: number | null) => boolean;
  selectedToMerge?: Folder | null;
  setSelectedToMerge?: React.Dispatch<React.SetStateAction<Folder | undefined>>;
}

export function FloatingActions<T>({
  selectedState,
  deleteRows,
  isDeleting,
  isMoving,
  moveRows,
  isMerging,
  gridApi,
  folders,
  selectedToMove,
  setSelectedToMove,
  currentFolderId,
  canMoveToFolder
}: Props<T>) {
  const { t } = useTranslation();
  const { getColour, theme } = useThemeContext();

  // Theme-aware colors
  const themeText = getColour('normalText');
  const themeLayer = getColour('secondLayer');
  const themeBorder = getColour('border');
  const activeColor = getColour('activeText');
  const pick = (light: string, dark: string) =>
    theme === 'dark' ? dark : light;
  const [visible, setVisible] = useState<boolean>(false);
  const [confirmationModal, setConfirmationModal] = useState<boolean>(false);
  const [mergeFilesModal, setMergeFilesModal] = useState<boolean>(false);
  const [filename, setFilename] = useState<string>('');
  const [selectedMergeOption, setSelectedMergeOption] = useState<number | null>(
    null
  );

  const [confirmationModalAll, setConfirmationModalAll] =
    useState<boolean>(false);
  const [moveMenu, setMoveMenu] = useState<boolean>(false);
  const [deletingItems, setDeletingItems] = useState<boolean>(false);

  const canMoveTo = (id: number | null) =>
    canMoveToFolder
      ? canMoveToFolder(selectedState ?? [], id)
      : id !== (currentFolderId ?? null);

  const mergeDocumentsMutation = useMergeDocuments();

  const isSelectAll = Array.isArray(selectedState)
    ? selectedState.length > 0 // TanStack selection (we just need >0)
    : !!(
        selectedState &&
        'selectAll' in selectedState &&
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (selectedState as any).selectAll
      );

  // And make mergeOptions defensive:
  const mergeOptions = (gridApi.getSelectedNodes?.() ?? [])
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .map((node: any) => node?.data)
    .filter(Boolean)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .map((data: any) => ({
      id: data.id,
      name: data.name || '',
      sessionId: data.session_id
    }))
    .filter(option => option.name);

  useEffect(() => {
    const timer = setTimeout(() => {
      setVisible(true);
    }, 150);
    return () => clearTimeout(timer);
  }, []);

  // When the merge modal opens, automatically set the first selected document as target
  useEffect(() => {
    if (mergeFilesModal) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const nodes = gridApi.getSelectedNodes().map(node => node.data) as any[];
      if (nodes.length > 0) {
        const firstDocument = nodes[0];
        const defaultFilename = firstDocument.name || '';
        setFilename(defaultFilename);
        // Automatically set first selected document as target
        setSelectedMergeOption(firstDocument.id);
      }
    } else {
      // Reset when modal closes
      setSelectedMergeOption(null);
      setFilename('');
    }
  }, [mergeFilesModal, gridApi]);

  const handleDeselect = () => {
    gridApi.deselectAll();
    gridApi.setServerSideSelectionState({
      selectAll: false,
      toggledNodes: []
    });
  };

  const handleDelete = async (type: 'selected' | 'all') => {
    setDeletingItems(true);
    await new Promise(resolve => setTimeout(resolve, 1000));

    if (deleteRows) {
      if (type === 'selected') {
        const nodes = selectedState?.length
          ? gridApi.getSelectedNodes().map(node => node.data as T)
          : gridApi.getRenderedNodes().map(node => node.data as T);

        if (nodes.length > 0) {
          await deleteRows(nodes, false);
          handleDeselect();
        } else {
          toast(t('no-rows-selected'));
        }
      } else {
        if (
          selectedState &&
          'selectAll' in selectedState &&
          selectedState.selectAll
        ) {
          await deleteRows([] as T[], true);
          gridApi.deselectAll();
          gridApi.setServerSideSelectionState({
            selectAll: false,
            toggledNodes: []
          });
        } else {
          toast(t('no-rows-selected'));
        }
      }
    } else {
      toast.error(t('delete-not-implemented'));
    }

    setDeletingItems(false);
  };

  const handleMove = (type: 'selected' | 'all') => {
    if (moveRows) {
      let nodes: T[];
      if (selectedState?.length) {
        nodes = gridApi.getSelectedNodes().map(node => node.data as T);
      } else {
        nodes = gridApi.getRenderedNodes().map(node => node.data as T);
      }
      if (nodes.length > 0) {
        moveRows(nodes, type === 'all').then(() => handleDeselect());
      } else {
        toast(t('no-rows-selected'));
      }
    } else {
      toast.error(t('move-not-implemented'));
    }
  };

  const handleMergeButtonClick = () => {
    const nodes = gridApi.getSelectedNodes().map(node => node.data) as T[];
    if (nodes.length === 0) {
      toast(t('no-rows-selected'));
      return;
    }
    if (nodes.length === 1) {
      toast.error(t('select-at-least-two-files-to-merge'));
      return;
    }
    if (nodes.length > 10) {
      toast.error(t('you-can-only-merge-up-to-10-files'));
      return;
    }
    const types = new Set(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      nodes.map((node: any) => (node.type || '').toLowerCase())
    );
    if (types.size > 1) {
      toast.error(t('you-cant-merge-transcript-files-with-caption-files'));
      return;
    }
    setMergeFilesModal(true);
  };

  const handleMergeConfirm = () => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const nodes = gridApi.getSelectedNodes().map(node => node.data) as any[];

    if (nodes.length < 2) {
      toast.error(t('select-at-least-two-files-to-merge'));
      return;
    }

    const targetNode = selectedMergeOption
      ? // eslint-disable-next-line @typescript-eslint/no-explicit-any
        nodes.find((node: any) => node.id === selectedMergeOption)
      : null;

    const sessionDocuments = nodes
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .filter((node: any) => !targetNode || node.id !== targetNode.id)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .map((node: any) => ({
        document_id: node.id,
        session_id: node.session_id
      }));

    const payload: DocumentPayload = {
      filename: filename || (targetNode ? targetNode.name : ''),
      session_documents: sessionDocuments,
      ...(targetNode && {
        target_document_id: targetNode.id,
        target_session_id: targetNode.session_id
      })
    };

    mergeDocumentsMutation.mutate(payload, {
      onSuccess: () => {
        toast.success(t('documents-merged-successfully'));
        handleDeselect();
      },
      onError: (error: AxiosError<BaseError>) => {
        const errorMessage =
          error.response?.data?.error_description ||
          error.response?.data?.detail ||
          error.response?.data?.error ||
          error.response?.data?.message ||
          t('an-error-occurred-while-merging-the-documents');
        toast.error(errorMessage);
      }
    });

    setMergeFilesModal(false);
  };

  return (
    <>
      <motion.div
        animate={{ y: visible ? 0 : -10 }}
        className="fixed bottom-4 inset-x-0 z-50 flex justify-center items-center"
      >
        <div
          className="shadow-lg border rounded-2xl border-gray-200"
          style={{
            borderColor: themeBorder,
            backgroundColor: pick('rgb(243 244 246)', 'rgb(39 39 42)')
          }}
        >
          <div className="flex justify-between items-center">
            {/* Left section: Selected count */}
            <div className="flex items-center">
              <div
                className="border-r text-white py-4 px-5 rounded-l-lg text-xs"
                style={{
                  borderColor: themeBorder,
                  backgroundColor: activeColor
                }}
              >
                <span className="font-semibold">
                  {selectedState &&
                  'selectAll' in selectedState &&
                  selectedState.selectAll
                    ? t('all')
                    : selectedState?.length || t('none')}{' '}
                </span>
                {t('selected')}
              </div>
            </div>

            {/* Center actions */}
            <div className="flex items-center gap-8 mx-4">
              <div
                onClick={handleMergeButtonClick}
                className={cn(
                  'flex flex-col items-center relative cursor-pointer',
                  isMerging || !isSelectAll
                    ? 'cursor-not-allowed opacity-50'
                    : 'hover:scale-105 transition-transform duration-150'
                )}
              >
                <Square width={16} className="text-primary" />
                <div className="text-primary text-[12px]">{t('merge')}</div>
              </div>
              {folders && (
                <button
                  type="button"
                  disabled={isMoving || !isSelectAll}
                  onClick={() => setMoveMenu(!moveMenu)}
                  className={cn(
                    'flex flex-col items-center relative cursor-pointer',
                    isMoving || !isSelectAll
                      ? 'cursor-not-allowed opacity-50'
                      : 'hover:scale-105 transition-transform duration-150'
                  )}
                >
                  <ExternalLink width={16} className="text-primary" />
                  <div className="text-primary text-[12px]">{t('move-to')}</div>
                </button>
              )}
              <div
                onClick={() => isSelectAll && setConfirmationModal(true)}
                className={cn(
                  'flex flex-col items-center cursor-pointer',
                  isDeleting || !isSelectAll
                    ? 'opacity-50 cursor-not-allowed'
                    : 'hover:scale-105 transition-transform duration-150'
                )}
              >
                <Trash2 width={16} className="text-red-500" />
                <div className="text-[12px] text-red-500">{t('delete')}</div>
              </div>
            </div>

            {/* Right section: Deselect */}
            <div
              onClick={handleDeselect}
              className="border-l px-2 cursor-pointer"
              style={{
                borderColor: themeBorder,
                color: themeText
              }}
            >
              <X width={16} />
            </div>
          </div>
        </div>
      </motion.div>
      {/* Merge Files Modal - Shows first selected document as automatic target */}
      <Modal
        isOpen={mergeFilesModal}
        onClose={() => setMergeFilesModal(false)}
        size="large"
        title={t('merge-documents')}
        caption={t('merge-modal-subtitle')}
        footerActions={[
          <Button
            onClick={() => setMergeFilesModal(false)}
            label={t('cancel')}
            size="small"
            variant="smoke"
          />,
          <Button
            onClick={handleMergeConfirm}
            label={t('merge')}
            size="small"
            variant="primary"
          />
        ]}
      >
        <div className="grid gap-4">
          {/* Info Banner - How merge works */}
          <div
            className="flex items-start gap-3 p-3 rounded-2xl border"
            style={{
              backgroundColor: pick('rgb(239 246 255)', 'rgb(30 58 138)'), // blue-50 / blue-900
              borderColor: pick('rgb(191 219 254)', 'rgb(59 130 246)'), // blue-200 / blue-500
              color: themeText
            }}
          >
            <AlertTriangle
              width={18}
              className="flex-shrink-0 mt-0.5"
              style={{ color: pick('rgb(37 99 235)', 'rgb(147 197 253)') }} // blue-600 / blue-300
            />
            <div className="flex-1 text-sm space-y-2">
              <p className="font-semibold">{t('merge-explanation-title')}</p>
              <ul className="space-y-1 text-xs opacity-90">
                <li>• {t('merge-explanation-1')}</li>
                <li>• {t('merge-explanation-2')}</li>
                <li>• {t('merge-explanation-3')}</li>
                <li>• {t('merge-explanation-4')}</li>
              </ul>
            </div>
          </div>

          {/* Merge Order Preview - Shows first selected as target */}
          {mergeOptions.length > 0 && (
            <div
              className="p-4 rounded-2xl border"
              style={{
                backgroundColor: pick('rgb(243 244 246)', 'rgb(39 39 42)'), // gray-100 / zinc-800
                borderColor: themeBorder
              }}
            >
              <p
                className="text-sm font-semibold mb-3"
                style={{ color: themeText }}
              >
                {t('merge-order-preview')}:
              </p>
              <div className="space-y-2">
                {mergeOptions.map((option, index) => {
                  const isTarget = index === 0; // First selected is always target
                  return (
                    <div
                      key={option.id}
                      className="flex items-center gap-3 p-2.5 rounded-2xl border"
                      style={{
                        backgroundColor: isTarget
                          ? pick('rgb(254 249 195)', 'rgb(113 63 18)') // yellow-100 / yellow-900
                          : themeLayer,
                        borderColor: isTarget
                          ? pick('rgb(251 191 36)', 'rgb(202 138 4)')
                          : themeBorder, // yellow-400 / yellow-600
                        color: themeText
                      }}
                    >
                      {/* Selection Number Badge */}
                      <div
                        className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold"
                        style={{
                          backgroundColor: isTarget
                            ? pick('rgb(251 191 36)', 'rgb(202 138 4)') // yellow-400 / yellow-600
                            : pick('rgb(229 231 235)', 'rgb(63 63 70)'), // gray-200 / zinc-700
                          color: isTarget ? 'white' : themeText
                        }}
                      >
                        {index + 1}
                      </div>

                      {/* Document Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 text-sm font-medium truncate">
                          <span className={isTarget ? 'font-bold' : ''}>
                            {option.name}
                          </span>
                          {isTarget && (
                            <span
                              className="text-xs px-2 py-0.5 rounded-full font-bold"
                              style={{
                                backgroundColor: pick(
                                  'rgb(251 191 36)',
                                  'rgb(202 138 4)'
                                ),
                                color: 'white'
                              }}
                            >
                              {t('target')}
                            </span>
                          )}
                        </div>
                        {isTarget && (
                          <p className="text-xs mt-1 opacity-75">
                            {t('merge-target-indicator')}
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Summary text */}
              <div
                className="mt-3 p-2 rounded-2xl text-xs"
                style={{
                  backgroundColor: pick('rgb(239 246 255)', 'rgb(30 64 175)'), // blue-50 / blue-900
                  color: themeText
                }}
              >
                <p>
                  •{' '}
                  {t('merge-summary-text', {
                    target: mergeOptions[0]?.name || '',
                    count: mergeOptions.length - 1
                  })}
                </p>
                <p className="mt-1">• {t('merge-originals-unchanged')}</p>
              </div>
            </div>
          )}

          {/* Optional: Filename Override */}
          <div
            className="flex sm:items-center sm:gap-x-1 py-2 border-t pt-4"
            style={{ borderColor: themeBorder }}
          >
            <label
              htmlFor="filename"
              className="block text-sm font-medium leading-6 w-32 flex-shrink-0"
              style={{ color: themeText }}
            >
              {t('output-filename')}:
            </label>
            <div className="flex-1">
              <Input
                id="filename"
                value={filename}
                onChange={e => setFilename(e.target.value)}
                type="text"
                placeholder={t('filename')}
                autoComplete="off"
                className="block w-full"
              />
              <p
                className="text-xs mt-1 opacity-60"
                style={{ color: themeText }}
              >
                {t('merge-filename-help')}
              </p>
            </div>
          </div>
        </div>
      </Modal>
      <Modal
        isOpen={confirmationModal}
        onClose={() => setConfirmationModal(false)}
        size="small"
        title={t('deletion-confirmaton')}
        caption={t('are-you-sure-you-want-to-delete-the-selected-items')}
        footerActions={[
          <Button
            onClick={() => setConfirmationModal(false)}
            label={t('cancel')}
            size={'small'}
            variant={'smoke'}
            disabled={deletingItems}
          />,
          <Button
            type={'button'}
            onClick={() => handleDelete('selected')}
            label={deletingItems ? t('deleting') : t('delete')}
            size={'small'}
            variant={'primary'}
            disabled={deletingItems}
          />
        ]}
        children={undefined}
      />{' '}
      <Modal
        isOpen={confirmationModalAll}
        onClose={() => setConfirmationModalAll(false)}
        size="small"
        title={t('deletion-confirmaton')}
        caption={t('are-you-sure-you-want-to-delete-the-selected-items')}
        footerActions={[
          <Button
            onClick={() => setConfirmationModalAll(false)}
            label={t('cancel')}
            size={'small'}
            variant={'smoke'}
            disabled={deletingItems}
          />,
          <Button
            type={'button'}
            onClick={() => handleDelete('all')}
            label={deletingItems ? t('deleting') : t('delete')}
            size={'small'}
            variant={'primary'}
            disabled={deletingItems}
          />
        ]}
        children={undefined}
      />
      <Modal
        isOpen={moveMenu}
        title={t('select-the-destination-folder')}
        onClose={() => setMoveMenu(false)}
        size="small"
        footerActions={[
          <Button
            onClick={() => setMoveMenu(false)}
            label={t('cancel')}
            size="small"
            variant="smoke"
          />,
          <Button
            onClick={() => handleMove('selected')}
            label={t('move')}
            disabled={isMoving || !canMoveTo(selectedToMove?.id ?? null)}
            size="small"
            variant="primary"
          />
        ]}
      >
        {moveMenu && (
          <MoveFolderPicker
            selectedFolder={selectedToMove}
            onSelect={folder => setSelectedToMove?.(folder)}
            canMoveTo={canMoveTo}
          />
        )}
      </Modal>
    </>
  );
}
